import React, { useState } from "react";
import { MapPin } from "lucide-react";
import { Input, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";
import { Field, StepShell } from "./_ui";
import type { StepProps } from "./types";

export function StepLocation({ state, setState, goNext, goBack }: StepProps) {
  const loc = (state.location ?? {}) as Record<string, string>;
  const [form, setForm] = useState({
    name: loc.name ?? "Main location",
    address_line_1: loc.address_line_1 ?? "",
    address_line_2: loc.address_line_2 ?? "",
    city: loc.city ?? "",
    state: loc.state ?? "",
    postal_code: loc.postal_code ?? "",
    country: loc.country ?? "US",
    phone: loc.phone ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const valid = [form.name, form.address_line_1, form.city, form.state, form.postal_code].every((v) => v.trim());

  const save = async () => {
    if (!valid) { toast.error("Please complete the address fields."); return; }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name.trim(), address_line_1: form.address_line_1.trim(),
        city: form.city.trim(), state: form.state.trim(),
        postal_code: form.postal_code.trim(), country: form.country.trim() || "US",
      };
      if (form.address_line_2.trim()) payload.address_line_2 = form.address_line_2.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      const next = await api.saveLocation(payload);
      setState(next);
      toast.success("Location saved");
      goNext();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <StepShell
      icon={MapPin}
      title="Where can customers find you?"
      description="Used for service area, directions and local context."
      onBack={goBack}
      onContinue={save}
      saving={saving}
      continueDisabled={!valid}
    >
      <Field label="Location name" required testid="field-loc-name">
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="onb-loc-name" />
      </Field>
      <Field label="Street address" required testid="field-address1">
        <Input value={form.address_line_1} onChange={(e) => set("address_line_1", e.target.value)}
          placeholder="123 Main St" data-testid="onb-address1" />
      </Field>
      <Field label="Suite / unit" testid="field-address2">
        <Input value={form.address_line_2} onChange={(e) => set("address_line_2", e.target.value)}
          placeholder="Suite 200" data-testid="onb-address2" />
      </Field>
      <div className="grid gap-5 sm:grid-cols-3">
        <Field label="City" required testid="field-city">
          <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="onb-city" />
        </Field>
        <Field label="State" required testid="field-state">
          <Input value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="IL" data-testid="onb-state" />
        </Field>
        <Field label="ZIP code" required testid="field-zip">
          <Input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} data-testid="onb-zip" />
        </Field>
      </div>
    </StepShell>
  );
}
